import { hasWindow } from "@omni/utils/dom.utils";
import { cn } from "@omni/utils/styles";
import React, {
    Children,
    cloneElement,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useState
} from "react";

import { Overlay } from "@/components/General/Overlay/overlay";
import { KeyNames } from "@/utils/enums";
import { ConditionalWrapper } from "@/utils/helpers";
import { createKeyHandler } from "@/utils/keyboard";

export type HeaderButtonProps = {
    children?: ReactElement<{ closeCallback?: () => void }> | false | null;
    icon: string;
    iconPrefix?: string;
    text?: string;
    hover?: boolean; // Use hover as a trigger
    alerts?: ReactNode;
    invert?: boolean;
    addCloseCallback?: boolean;
    hideChevron?: boolean;
    withOverlay?: boolean;
    link?: string;
    hiddenText?: boolean;
    ariaLabel?: string;
    contentClassName?: string;
    buttonClassName?: string;
    test?: string;
};

const HeaderButton = ({
    icon,
    alerts,
    text,
    children,
    addCloseCallback,
    hover,
    invert,
    link,
    iconPrefix = "las",
    hideChevron = false,
    hiddenText = false,
    withOverlay = false,
    ariaLabel,
    contentClassName,
    buttonClassName,
    test
}: HeaderButtonProps) => {
    const [childElement, setChildElement] = useState<ReactNode>(null);
    const [isActive, setActiveState] = useState(false);
    const [preventBlur, setPreventBlur] = useState(false);

    const setActive = useCallback((active: boolean = true) => {
        if (active && hasWindow()) {
            window?.dispatchEvent(new Event("header-button-activated"));
        }

        setActiveState(active);
    }, []);

    const handleOnActivated = useCallback(() => {
        setActive(false);
    }, [setActive]);

    const handleOnKeyDown = createKeyHandler([
        {
            keys: [KeyNames.ESCAPE],
            handler: () => setActive(false)
        }
    ]);

    const closePopupWindow = useCallback(() => {
        setActive(false);
        setPreventBlur(false);
    }, [setActive]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener("header-button-activated", handleOnActivated, {
            signal: abortController.signal
        });
        window.addEventListener("keydown", handleOnKeyDown, {
            signal: abortController.signal
        });

        setChildElement(
            addCloseCallback && children
                ? cloneElement(Children.only(children), {
                      closeCallback: closePopupWindow
                  })
                : children
        );

        return () => {
            abortController.abort();
        };
    }, [
        addCloseCallback,
        children,
        closePopupWindow,
        handleOnKeyDown,
        handleOnActivated
    ]);

    const onMouseEnter = () => {
        if (hover && children) {
            setActive();
        }
        setPreventBlur(true);
    };

    const onMouseLeave = () => {
        if (hover && children) {
            setActive(false);
        }
        setPreventBlur(false);
    };

    const onClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        if (!hover && children) {
            e.preventDefault();
            setActive(!isActive);
            setPreventBlur(true);
        }
    };

    const onBlur = () => {
        if (!preventBlur) {
            setActive(false);
        }
    };

    return (
        <div
            className={cn("tw:relative", {
                "tw:layer-modal/[1]": withOverlay && isActive
            })}
            data-test={test || `header-button-${text}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onBlur={onBlur}
        >
            <a
                className={cn(
                    "tw:relative tw:text-primary! tw:whitespace-nowrap",
                    "tw:hover:cursor-pointer",
                    "tw:lg:p-0",
                    "tw:hover:no-underline!",
                    {
                        "tw:z-20": isActive,
                        "tw:lg:hover:text-primary-focus! tw:lg:focus:text-primary-focus!":
                            !invert,
                        "tw:text-white! tw:focus:text-white! tw:hover:text-white!":
                            invert &&
                            (!childElement || (!!childElement && !isActive)),
                        "tw:text-primary-focus! tw:focus:text-primary-focus! tw:hover:text-primary-focus!":
                            invert && withOverlay && !!childElement && isActive
                    },
                    buttonClassName
                )}
                data-test={`${test || `header-button-${text}`}-button`}
                href={link}
                onClick={onClick}
                aria-label={ariaLabel}
            >
                <i
                    className={cn(
                        "tw:relative tw:text-3xl! tw:mr-1 tw:mb-1 tw:align-middle",
                        `${iconPrefix} la-${icon}`
                    )}
                >
                    {!!alerts && (
                        <span
                            className={cn(
                                "tw:rounded-full tw:px-0.5 tw:min-w-5 tw:min-h-5 tw:absolute tw:-right-1 tw:text-sm tw:leading-5 tw:font-semibold tw:text-center tw:text-white tw:bg-success"
                            )}
                        >
                            {alerts}
                        </span>
                    )}
                </i>
                <span
                    className={cn({
                        "tw:hidden tw:lg:inline": !isActive,
                        "tw:inline": isActive,
                        "sr-only": hiddenText
                    })}
                >
                    {text}
                </span>
                {!hover && children && !hideChevron && (
                    <i
                        className={cn(
                            "las la-angle-down",
                            "tw:transition-transform tw:ml-2 tw:h-full tw:items-center",
                            {
                                "tw:rotate-180": isActive
                            }
                        )}
                    />
                )}
            </a>
            {childElement && (
                <ConditionalWrapper
                    condition={withOverlay && !!childElement}
                    wrapper={(children) => {
                        return (
                            <Overlay
                                open={isActive}
                                overlayHeader={true}
                                scrollLock={true}
                                onClose={closePopupWindow}
                            >
                                <Overlay.Content>{children}</Overlay.Content>
                            </Overlay>
                        );
                    }}
                >
                    <div
                        className={cn(
                            "tw:z-10 tw:hidden tw:absolute tw:top-0 tw:right-0 tw:w-100 tw:max-w-dvw tw:h-full tw:pt-9 tw:px-4 tw:pb-4 tw:border tw:border-grey-light tw:shadow-xl tw:bg-background tw:origin-top-right",
                            "tw:lg:-top-2 tw:lg:-right-2 tw:lg:h-fit tw:lg:rounded",
                            {
                                "tw:block tw:animate-popover": isActive,
                                "tw:layer-modal/[1]": withOverlay && isActive
                            },
                            contentClassName
                        )}
                    >
                        {childElement}
                    </div>
                </ConditionalWrapper>
            )}
        </div>
    );
};

export { HeaderButton };
